import { useRef } from "preact/hooks";

const MIN_SIDEBAR_WIDTH = 160;
const MAX_SIDEBAR_WIDTH = 600;

export type ResizeHandleProps = {
  slot: "lhs" | "rhs";
  onResize: (widthPx: number, commit: boolean) => void;
};

/**
 * A sidebar's draggable edge, for a caller whose dock grows/shrinks by width.
 * Rendered as a child of the dock it resizes -- that parent's width is what a
 * drag starts from.
 */
export function ResizeHandle({ slot, onResize }: ResizeHandleProps) {
  const dragging = useRef<{ startX: number; startWidth: number } | null>(null);
  const raf = useRef<number | undefined>(undefined);
  const latestWidth = useRef<number | undefined>(undefined);

  function onPointerMove(e: PointerEvent) {
    const start = dragging.current;
    if (!start) return;
    // lhs grows to the right (toward the editor); rhs grows to the left.
    const sign = slot === "lhs" ? 1 : -1;
    const width = Math.min(
      MAX_SIDEBAR_WIDTH,
      Math.max(
        MIN_SIDEBAR_WIDTH,
        start.startWidth + sign * (e.clientX - start.startX),
      ),
    );
    latestWidth.current = width;
    if (raf.current === undefined) {
      raf.current = requestAnimationFrame(() => {
        raf.current = undefined;
        if (latestWidth.current !== undefined) {
          onResize(latestWidth.current, false);
        }
      });
    }
  }

  function endDrag(e: PointerEvent, commit: boolean) {
    const target = e.currentTarget as HTMLElement;
    // A pointercancel has already implicitly released capture; guard so
    // that doesn't throw ("no capture to release") in that path.
    if (target.hasPointerCapture(e.pointerId)) {
      target.releasePointerCapture(e.pointerId);
    }
    if (raf.current !== undefined) {
      cancelAnimationFrame(raf.current);
      raf.current = undefined;
    }
    if (commit && latestWidth.current !== undefined) {
      onResize(latestWidth.current, true);
    }
    dragging.current = null;
    latestWidth.current = undefined;
  }

  function onPointerUp(e: PointerEvent) {
    endDrag(e, true);
  }

  function onPointerCancel(e: PointerEvent) {
    // e.g. the OS hands the gesture to something else mid-drag -- drop the
    // in-progress resize without committing an uncommitted width.
    endDrag(e, false);
  }

  function onPointerDown(e: PointerEvent) {
    if (e.button !== 0) return; // primary button (or touch) only
    const dock = (e.currentTarget as HTMLElement).parentElement;
    dragging.current = {
      startX: e.clientX,
      startWidth: dock?.getBoundingClientRect().width ?? MIN_SIDEBAR_WIDTH,
    };
    // Capture keeps delivering events after the drag leaves the narrow handle.
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    e.preventDefault();
  }

  return (
    <div
      class={`sb-resizer sb-resizer-${slot}`}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
    />
  );
}
